import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useHasRole } from "@/components/RoleGate";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_BADGES = {
  sent: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  mocked: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  failed: "bg-red-100 text-red-800 hover:bg-red-100",
};

export default function EmailOutbox() {
  const { t, i18n } = useTranslation();
  const canResend = useHasRole("admin");
  const language = i18n.language?.split("-")[0] || "en";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      params.set("page_size", "100");
      const { data } = await api.get(`/email-outbox?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err, t("emailOutbox.error")));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function resend(entryId) {
    setResending(entryId);
    try {
      await api.post(`/email-outbox/${entryId}/resend`);
      toast.success(t("emailOutbox.resent"));
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("emailOutbox.error")));
    } finally {
      setResending(null);
    }
  }

  return (
    <div className="space-y-8" data-testid="email-outbox-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-heading text-4xl tracking-tight text-slate-900">
            {t("emailOutbox.title")}
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-slate-500">
            {t("emailOutbox.subtitle")}
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[180px]">
          <Select
            value={statusFilter || "all"}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
          >
            <SelectTrigger data-testid="email-outbox-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("emailOutbox.allStatuses")}</SelectItem>
              <SelectItem value="sent">{t("emailOutbox.status.sent")}</SelectItem>
              <SelectItem value="mocked">{t("emailOutbox.status.mocked")}</SelectItem>
              <SelectItem value="failed">{t("emailOutbox.status.failed")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ms-auto text-xs text-slate-500">
          {t("common.totalCount", { count: total })}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("emailOutbox.table.when")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("emailOutbox.table.to")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("emailOutbox.table.subject")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("emailOutbox.table.language")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("emailOutbox.table.status")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("emailOutbox.table.invoice")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("common.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">
                  {t("emailOutbox.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((it) => (
                <TableRow key={it.id} data-testid={`outbox-row-${it.id}`}>
                  <TableCell className="text-slate-600 text-xs">
                    {formatDate(it.created_at, language)}
                  </TableCell>
                  <TableCell className="text-slate-700 text-sm">{it.to_email || "—"}</TableCell>
                  <TableCell className="max-w-md truncate text-slate-800 text-sm">
                    {it.subject}
                  </TableCell>
                  <TableCell className="text-slate-500 uppercase text-xs">{it.language}</TableCell>
                  <TableCell>
                    <Badge
                      className={STATUS_BADGES[it.status] || STATUS_BADGES.failed}
                      data-testid={`outbox-status-${it.id}`}
                    >
                      {t(`emailOutbox.status.${it.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {it.invoice_id ? (
                      <Link
                        to={`/app/invoices/${it.invoice_id}`}
                        className="font-mono text-xs text-slate-700 hover:underline"
                      >
                        {t("emailOutbox.viewInvoice")}
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-end">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setView(it)}
                        data-testid={`outbox-view-${it.id}`}
                        className="gap-1"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {t("emailOutbox.view")}
                      </Button>
                      {canResend && it.invoice_id && it.status !== "sent" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={resending === it.id}
                          onClick={() => resend(it.id)}
                          data-testid={`outbox-resend-${it.id}`}
                          className="gap-1 text-slate-700"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          {t("emailOutbox.resend")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent
          className="max-h-[85vh] max-w-3xl overflow-hidden p-0"
          data-testid="outbox-view-dialog"
        >
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="text-base">{view?.subject}</DialogTitle>
            <DialogDescription className="text-xs">
              {view?.to_email} · {view?.language?.toUpperCase()} ·{" "}
              {view && formatDate(view.created_at, language)}
              {view?.status && (
                <Badge className={`ms-2 ${STATUS_BADGES[view.status] || ""}`}>
                  {view.status}
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>
          {view?.error && (
            <div className="mx-6 rounded-md border border-red-200 bg-red-50 p-3 text-xs font-mono text-red-800">
              {view.error}
            </div>
          )}
          <div className="overflow-y-auto px-6 pb-6" style={{ maxHeight: "calc(85vh - 140px)" }}>
            {view?.body_html ? (
              <iframe
                title="email-body"
                srcDoc={view.body_html}
                sandbox=""
                className="min-h-[400px] w-full rounded-md border border-slate-200"
                data-testid="outbox-iframe"
              />
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-slate-700">{view?.body_text}</pre>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
